import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  FileText, 
  Calendar, 
  Clock, 
  User, 
  ArrowRight,
  Search
} from 'lucide-react'
import { blogPosts } from '../lib/data'

const InsightsPage = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTag, setSelectedTag] = useState<string>('')
  
  // Get unique tags
  const allTags = Array.from(new Set(blogPosts.flatMap(post => post.tags)))
  
  // Filter posts
  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesTag = !selectedTag || post.tags.includes(selectedTag)
    return matchesSearch && matchesTag
  })

  return (
    <>
      <Helmet>
        <title>Insights & Blog - DevSecRoot</title>
        <meta name="description" content="Stay ahead with our latest security and development insights, best practices, and industry trends." />
        <link rel="canonical" href="https://devsecroot.com/insights" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-dsr-text mb-6">
              Insights & Blog
            </h1>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              Stay ahead with our latest security and development insights, best practices, 
              and industry trends. Knowledge is power in the world of secure development.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Search and Filter Section */}
      <section className="section-padding">
        <div className="container-max">
          <div className="mb-12">
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-8">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-dsr-text/50" />
                <input
                  type="text"
                  placeholder="Search articles..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-dsr-card border border-white/10 rounded-xl text-dsr-text placeholder-dsr-text/50 focus:border-dsr-accent focus:outline-none transition-colors"
                />
              </div>
            </div>

            {/* Tag Filter */}
            <div className="flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setSelectedTag('')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  selectedTag === ''
                    ? 'bg-dsr-accent text-black'
                    : 'bg-dsr-card border border-white/10 text-dsr-text/70 hover:text-dsr-text hover:border-dsr-accent/30'
                }`}
              >
                All Topics
              </button>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedTag === tag
                      ? 'bg-dsr-accent text-black'
                      : 'bg-dsr-card border border-white/10 text-dsr-text/70 hover:text-dsr-text hover:border-dsr-accent/30'
                  }`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Articles Grid */}
          <motion.div
            key={`${searchTerm}-${selectedTag}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {filteredPosts.map((post, index) => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="glass-card-hover overflow-hidden h-full flex flex-col"
              >
                <div className="h-48 bg-gradient-to-br from-dsr-accent/20 to-cyan-500/20 flex items-center justify-center">
                  <FileText className="w-16 h-16 text-dsr-accent" />
                </div>
                
                <div className="p-6 flex-grow flex flex-col">
                  <div className="flex items-center space-x-2 mb-3">
                    {post.tags.slice(0, 2).map((tag) => (
                      <span key={tag} className="px-3 py-1 rounded-full text-xs font-medium bg-dsr-accent/20 text-dsr-accent">
                        {tag}
                      </span>
                    ))}
                  </div>
                  
                  <h3 className="text-xl font-semibold text-dsr-text mb-3">{post.title}</h3>
                  <p className="text-dsr-text/70 mb-4 flex-grow">{post.description}</p>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center space-x-2 text-sm text-dsr-text/60">
                      <User className="w-4 h-4" />
                      <span>{post.author.name}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-dsr-text/60">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(post.publishedAt).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center space-x-2 text-sm text-dsr-text/60">
                      <Clock className="w-4 h-4" />
                      <span>{post.readingTime} min read</span>
                    </div>
                  </div>
                  
                  <Link 
                    to={`/insights/${post.slug}`}
                    className="inline-flex items-center space-x-2 text-dsr-accent hover:text-cyan-400 transition-colors mt-auto"
                  >
                    <span>Read article</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {filteredPosts.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <p className="text-dsr-text/70 text-lg">
                No articles found matching your criteria.
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-dsr-card/30">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Stay Updated with Security & Development
            </h2>
            <p className="text-xl text-dsr-text/70 mb-8 max-w-2xl mx-auto">
              Get the latest insights delivered to your inbox and stay ahead of emerging threats and technologies.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                Subscribe to Updates
              </Link>
              <Link to="/case-studies" className="btn-secondary text-lg px-8 py-4">
                View Case Studies
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default InsightsPage
